import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { COMPANY } from '../lib/site';
type Phase = 'gather' | 'reveal' | 'explode' | 'done';
type ConnectionNavigator = Navigator & {
  connection?: {
    saveData?: boolean;
  };
};
function shouldSkipPreloader() {
  const connection = navigator as ConnectionNavigator;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches || connection.connection?.saveData === true;
}
export function Preloader({
  onDone
}: {
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('gather');
  const particles = useMemo(() => Array.from({
    length: 32
  }).map((_, index) => {
    const angle = index / 32 * Math.PI * 2;
    const radius = 140 + Math.random() * 160;
    return {
      id: index,
      startX: Math.cos(angle) * radius,
      startY: Math.sin(angle) * radius,
      size: 2 + Math.random() * 3,
      fire: Math.random() > 0.84,
      explodeX: Math.cos(angle) * (380 + Math.random() * 260),
      explodeY: Math.sin(angle) * (380 + Math.random() * 260),
      delay: Math.random() * 0.16
    };
  }), []);
  useEffect(() => {
    const finish = () => {
      try {
        sessionStorage.setItem('dream-shield-preloader-seen', 'true');
      } catch {
        // Storage can be unavailable in private browsing contexts.
      }
      onDone();
    };
    if (shouldSkipPreloader()) {
      finish();
      return;
    }
    const revealTimer = window.setTimeout(() => setPhase('reveal'), 360);
    const explodeTimer = window.setTimeout(() => setPhase('explode'), 820);
    const doneTimer = window.setTimeout(() => setPhase('done'), 1060);
    const finishTimer = window.setTimeout(finish, 1280);
    return () => {
      window.clearTimeout(revealTimer);
      window.clearTimeout(explodeTimer);
      window.clearTimeout(doneTimer);
      window.clearTimeout(finishTimer);
    };
  }, [onDone]);
  return <AnimatePresence data-id="element-130" data-mp="23nungelpqiil">
      {phase !== 'done' && <motion.div className="fixed inset-0 z-[10000] flex items-center justify-center bg-white" exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} data-id="element-131" data-mp="23mb2wbt0xlb0">
          <motion.div className="absolute h-52 w-52 rounded-full bg-[radial-gradient(circle,rgba(45,168,255,0.28),transparent_70%)] blur-xl" animate={{
        opacity: phase === 'explode' ? 0 : 1,
        scale: phase === 'reveal' ? 1.08 : 1
      }} transition={{
        duration: 0.36
      }} data-id="element-132" data-mp="28m5ukhkmiex4" />

          <div className="relative flex flex-col items-center" data-id="element-133" data-mp="2rf9rcbt517vy">
            <div className="relative flex h-40 w-40 items-center justify-center" data-id="element-134" data-mp="11zaeajmeeyb1">
              {particles.map(particle => <motion.span key={particle.id} className="absolute rounded-full" style={{
            width: particle.size,
            height: particle.size,
            background: particle.fire ? '#FF5A36' : '#2DA8FF'
          }} initial={{
            x: particle.startX,
            y: particle.startY,
            opacity: 0
          }} animate={phase === 'explode' ? {
            x: particle.explodeX,
            y: particle.explodeY,
            opacity: 0
          } : {
            x: 0,
            y: 0,
            opacity: [0, 1, 0.25]
          }} transition={{
            duration: phase === 'explode' ? 0.34 : 0.42,
            delay: particle.delay,
            ease: phase === 'explode' ? 'easeOut' : 'easeInOut'
          }} data-id="element-135" data-mp="x0z427s6ylsa" />)}
              <motion.div className="absolute h-28 w-28 rounded-full border border-electric/40" animate={{
            rotate: 360,
            opacity: phase === 'explode' ? 0 : 1
          }} transition={{
            rotate: {
              duration: 2.8,
              repeat: Infinity,
              ease: 'linear'
            }
          }} data-id="element-136" data-mp="2aa51zf2hsso0" />
              <motion.img src={COMPANY.logo} alt="" decoding="async" className="relative z-10 h-24 w-24 object-contain" initial={{
            opacity: 0,
            scale: 0.72
          }} animate={{
            opacity: phase === 'gather' ? 0.3 : 1,
            scale: phase === 'explode' ? 1.1 : 1
          }} transition={{
            duration: 0.38,
            ease: 'easeOut'
          }} data-id="element-137" data-mp="1ricajp32nfie" />
            </div>
            <motion.p className="mt-4 font-display text-lg font-bold tracking-[0.25em] text-navy" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: phase === 'gather' ? 0 : 1,
          y: 0
        }} transition={{
          duration: 0.25
        }} data-id="element-138" data-mp="3p9kez8c2xnd5">
              DREAM SHIELD
            </motion.p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}